import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AdmissionCard } from '@components/admission-card/src/admission-card';
import {
  ADMISSION_CARD_TAG_ACCESS_CODE,
  ADMISSION_CARD_TAG_IMPORT_ONLY,
  AdmissionCardTag,
} from '@components/admission-card/src/admission-card.type';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { BottleService, GuestList, Ticket } from '@domain/admission';
import { useGetAdmissions } from '@hooks/admissions/use-get-admissions';
import { getAdmissionDDPPrice } from '@utils/get-admission-ddp-price/get-admission-ddp-price';
import { getAdmissionOnlinePrice } from '@utils/get-admission-online-price/get-admission-online-price';
import classNames from 'classnames';

import { Props } from './select-trigger-offer-modal.type';

export const SelectTriggerOfferModal = ({
  handleClose,
  setSelectedOfferTrigger,
  admissionType,
  eventId,
  timezone,
  offerId,
  setAfterOfferStartDate,
}: Props) => {
  const { t } = useTranslation();
  const [offersAvailable, setOffersAvailable] = useState<boolean>(false);

  const { data: admissions } = useGetAdmissions({ eventId, options: { enabled: !!eventId } });

  const handleAdmissionClick = (admission: BottleService | GuestList | Ticket): void => {
    setAfterOfferStartDate(admission.salesTime.closingTime);
    setSelectedOfferTrigger(admission);
    handleClose(true);
  };

  const handleCrossClose = () => {
    handleClose(false);
  };

  return (
    <div data-test-id='select-trigger-offer-modal--container'>
      <ModalStickyHeader title={t('offers:selectAnOffer')} handleClose={handleCrossClose} bordered={true} />

      <div className='flex flex-col justify-center items-center px-4 md:px-10 py-6'>
        {!offersAvailable && (
          <div
            data-testid='select-trigger-offer-modal--offer-not-avalaible'
            className='flex justify-center items-center h-12 text-subtitle-s text-primary'>
            {t('offers:offerNotAvailable')}
          </div>
        )}
        <div className={classNames('mt-5 space-y-3 w-full', { hidden: !offersAvailable })}>
          {admissions?.[admissionType].map((admission) => {
            if (admission.salesStatus.isSalesClosed || admission.salesStatus.isSoldOut || admission.id === offerId)
              return null;

            if (!offersAvailable) setOffersAvailable(true);
            const onlinePrice = getAdmissionOnlinePrice(admission);
            const ddpPrice = getAdmissionDDPPrice(admission);
            let tags: AdmissionCardTag[] = admission.accessCode ? [ADMISSION_CARD_TAG_ACCESS_CODE] : [];

            if (admission.settings.isPrivate) tags = [...tags, ADMISSION_CARD_TAG_IMPORT_ONLY];

            return (
              <AdmissionCard
                key={admission.id}
                id={admission.id}
                timezone={timezone}
                color={admission.color ?? ''}
                name={admission.displayName}
                description={admission.displayDescription}
                quantity={admission.quantity}
                soldItems={0}
                currency={admission.price.currency}
                amount={admission.price.amount}
                isPrivate={admission.settings.isPrivate}
                isPublished={false}
                needsUserVerified={false}
                salesEnds={0}
                tags={tags}
                status={admission.status}
                showSubmenuIcon={false}
                onClick={() => handleAdmissionClick(admission)}
                guestCapacity={(admission as BottleService).guestCapacity}
                isPreset={true}
                onlinePrice={onlinePrice}
                ddpPrice={ddpPrice}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
